import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { sshAPI } from '../services/api';

const steps = ['Server Details', 'Authentication', 'Test & Complete'];

function SSHSetupWizard({ open, onComplete }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    host: '',
    port: 22,
    username: '',
    auth_method: 'password',
    password: '',
    private_key: '',
    passphrase: '',
    connection_timeout: 30,
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError('');
  };

  const handleNext = async () => {
    setError('');

    // Validation for each step
    if (activeStep === 0) {
      if (!formData.host || !formData.username) {
        setError('Host and username are required');
        return;
      }
    } else if (activeStep === 1) {
      if (formData.auth_method === 'password' && !formData.password) {
        setError('Password is required for password authentication');
        return;
      }
      if (
        (formData.auth_method === 'key' || formData.auth_method === 'key_with_passphrase') &&
        !formData.private_key
      ) {
        setError('Private key is required for key authentication');
        return;
      }
    } else if (activeStep === 2) {
      // Final step - test connection and save
      await handleTestAndSave();
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError('');
    setTestResult(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleTestAndSave = async () => {
    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      // Build config data
      const configData = {
        host: formData.host,
        port: formData.port,
        username: formData.username,
        auth_method: formData.auth_method,
        connection_timeout: formData.connection_timeout,
      };

      if (formData.auth_method === 'password') {
        configData.password = formData.password;
      } else if (formData.auth_method === 'key') {
        configData.private_key = formData.private_key;
      } else if (formData.auth_method === 'key_with_passphrase') {
        configData.private_key = formData.private_key;
        configData.passphrase = formData.passphrase;
      }

      // Call setup wizard endpoint (tests connection and saves)
      const response = await sshAPI.setupWizard(configData);

      setTestResult({
        success: true,
        message: response.data.message || 'SSH configuration saved and tested successfully',
      });

      // Wait 2 seconds to show success message, then complete
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.details ||
        err.response?.data?.error ||
        'Failed to configure SSH connection';

      setTestResult({
        success: false,
        message: errorMessage,
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Step 1: SSH Server Connection Details
              </Typography>
              <Typography variant="body2">
                These are the <strong>SSH credentials</strong> used to connect to your remote Samba4 AD/DC server.
                This is NOT your domain administrator password - you'll configure that later using the "Sudo" button.
              </Typography>
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Samba4 Server Hostname or IP Address"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.host}
              onChange={handleChange('host')}
              disabled={loading}
              required
              sx={{ mb: 2 }}
              helperText="IP address or hostname of the remote server running Samba4 AD/DC (e.g., 192.168.1.100)"
            />
            <TextField
              margin="dense"
              label="SSH Port"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.port}
              onChange={handleChange('port')}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="Default is 22"
            />
            <TextField
              margin="dense"
              label="SSH Username (Server OS User)"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.username}
              onChange={handleChange('username')}
              disabled={loading}
              required
              helperText="Linux/Unix username on the remote server (e.g., tacit, ubuntu, admin) - NOT domain admin"
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Step 2: SSH Authentication Method
              </Typography>
              <Typography variant="body2">
                Choose how to authenticate to the SSH server. These credentials will be encrypted and stored securely.
              </Typography>
            </Alert>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">SSH Authentication Method</FormLabel>
              <RadioGroup
                value={formData.auth_method}
                onChange={handleChange('auth_method')}
              >
                <FormControlLabel
                  value="password"
                  control={<Radio />}
                  label="Password Authentication"
                  disabled={loading}
                />
                <FormControlLabel
                  value="key"
                  control={<Radio />}
                  label="SSH Key (no passphrase)"
                  disabled={loading}
                />
                <FormControlLabel
                  value="key_with_passphrase"
                  control={<Radio />}
                  label="SSH Key (with passphrase)"
                  disabled={loading}
                />
              </RadioGroup>
            </FormControl>

            {formData.auth_method === 'password' && (
              <TextField
                margin="dense"
                label="SSH Password (Server OS Password)"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={handleChange('password')}
                disabled={loading}
                required
                helperText="Password for the SSH user specified in Step 1 - used to connect to remote server"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {(formData.auth_method === 'key' ||
              formData.auth_method === 'key_with_passphrase') && (
              <>
                <TextField
                  margin="dense"
                  label="Private Key"
                  multiline
                  rows={8}
                  fullWidth
                  variant="outlined"
                  value={formData.private_key}
                  onChange={handleChange('private_key')}
                  disabled={loading}
                  required
                  helperText="Paste your SSH private key (e.g., id_rsa)"
                  sx={{ mb: 2, fontFamily: 'monospace' }}
                />
                {formData.auth_method === 'key_with_passphrase' && (
                  <TextField
                    margin="dense"
                    label="Key Passphrase"
                    type={showPassphrase ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={formData.passphrase}
                    onChange={handleChange('passphrase')}
                    disabled={loading}
                    helperText="Passphrase to decrypt the private key"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle passphrase visibility"
                            onClick={() => setShowPassphrase(!showPassphrase)}
                            edge="end"
                          >
                            {showPassphrase ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review your configuration and test the connection.
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Configuration Summary:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Server:</strong> {formData.username}@{formData.host}:{formData.port}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Auth Method:</strong>{' '}
                {formData.auth_method === 'password'
                  ? 'Password'
                  : formData.auth_method === 'key'
                  ? 'SSH Key'
                  : 'SSH Key with Passphrase'}
              </Typography>
            </Box>

            {testResult && (
              <Alert
                severity={testResult.success ? 'success' : 'error'}
                icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                sx={{ mb: 2 }}
              >
                {testResult.message}
                {testResult.success && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    SSH configuration will be saved. You can now manage your Samba4 server
                    remotely!
                  </Typography>
                )}
              </Alert>
            )}

            {!testResult && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Click "Test & Save" to verify the connection and save your configuration.
              </Alert>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          // Only allow closing via cancel button
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>SSH Server Configuration</span>
          <Chip label="Required Setup" color="primary" size="small" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && activeStep !== 2 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || loading}>
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading || (testResult && !testResult.success)}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : activeStep === steps.length - 1 ? (
            testResult?.success ? (
              'Completing...'
            ) : (
              'Test & Save'
            )
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SSHSetupWizard;
